import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../utils/theme';

const UpdateNotification = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: #fff;
  border: 1px solid ${theme.neutral.border.main};
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 99999;
  max-width: 400px;
`;

const UpdateButton = styled.button`
  background: ${theme.brand.primary};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin-right: 8px;
  opacity: ${({ disabled }) => (disabled ? 0.8 : 1)};

  &:hover {
    background: ${({ disabled }) => (disabled ? theme.brand.primary : theme.brand.primaryLight)};
  }
`;

const DismissButton = styled.button`
  background: ${theme.neutral.bg.main};
  color: ${theme.neutral.text.secondary};
  border: 1px solid ${theme.neutral.border.main};
  padding: 8px 16px;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    background: ${({ disabled }) => (disabled ? theme.neutral.bg.main : theme.neutral.bg.secondary)};
  }
`;

const ProgressBar = styled.div`
  margin-top: 12px;
  height: 4px;
  background: ${theme.neutral.bg.secondary};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.brand.primary};
  border-radius: 2px;
  width: ${({ progress }) => progress}%;
  transition: width 0.3s ease;
`;

const VersionCheck = ({
  newVersionAvailable,
  currentVersion,
  latestVersion,
  changelog = [],
  onUpdate,
  onDismiss,
  isDismissed = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  if (!newVersionAvailable || isDismissed) {
    return null;
  }

  const handleUpdate = async () => {
    setLoading(true);
    setProgress(5);

    // Simulate progress while cache clearing / SW unregistration runs
    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(intervalRef.current);
          return 90;
        }
        return prev + Math.random() * 12;
      });
    }, 300);

    try {
      await onUpdate();
    } finally {
      clearInterval(intervalRef.current);
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <UpdateNotification>
      <div style={{ marginBottom: '12px' }}>
        <strong>Nova verzija je dostupna!</strong>
      </div>
      <div
        style={{
          marginBottom: '12px',
          fontSize: '14px',
          color: '#666',
        }}
      >
        Trenutna verzija: {currentVersion}
        <br />
        Nova verzija: {latestVersion}
      </div>

      {changelog.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '6px' }}>
            Šta je novo:
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '13px',
              color: '#666',
              maxHeight: '120px',
              overflowY: 'auto',
            }}
          >
            {changelog.map((change, index) => (
              <li key={index} style={{ marginBottom: '3px' }}>
                {change}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <UpdateButton onClick={handleUpdate} disabled={loading}>
          {loading ? 'Ažuriranje...' : 'Ažuriraj sada'}
        </UpdateButton>
        <DismissButton onClick={onDismiss} disabled={loading}>
          Kasnije
        </DismissButton>
      </div>

      {loading && (
        <ProgressBar>
          <ProgressFill progress={Math.min(progress, 100)} />
        </ProgressBar>
      )}
    </UpdateNotification>
  );
};

export default VersionCheck;
